/**
 * App.jsx - メインアプリケーション
 * ルーティング & 認証ガード
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { userApi } from './services/userApi';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import DeviceList from './pages/DeviceList';
import DeviceDetail from './pages/DeviceDetail';
import Provision from './pages/Provision';
import Groups from './pages/Groups';
import FirmwareVersions from './pages/FirmwareVersions';
import OTAJobs from './pages/OTAJobs';
import OTAJobDetail from './pages/OTAJobDetail';
import DeviceFirmware from './pages/DeviceFirmware';
import Pipeline from './pages/Pipeline';
import SiTLTests from './pages/SiTLTests';
import HiLSTests from './pages/HiLSTests';
import ThingVersions from './pages/ThingVersions';
import FlightLogs from './pages/FlightLogs';
import OrganizationSettings from './pages/OrganizationSettings';
import Register from './pages/Register';
import AcceptInvite from './pages/AcceptInvite';
import Admin from './pages/Admin';


/**
 * 認証ガード: ログインしていない場合は /login にリダイレクト
 */
function ProtectedRoute({ children }) {
  const { isLoggedIn, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

/**
 * ログイン済みの場合は / にリダイレクト
 */
function PublicRoute({ children }) {
  const { isLoggedIn, loading } = useAuth();

  if (loading) return null;

  if (isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/**
 * superadmin専用ルートガード
 */
function SuperadminRoute({ children }) {
  const { isLoggedIn, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    if (isLoggedIn) {
      userApi.me()
        .then(setProfile)
        .catch(() => setProfile(null))
        .finally(() => setProfileLoading(false));
    } else {
      setProfileLoading(false);
    }
  }, [isLoggedIn]);

  if (authLoading || profileLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  // API から取得したプロフィールの role をチェック
  if (profile?.role !== 'superadmin') {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* ログイン */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      {/* 新規登録 ← ここに追加（ProtectedRouteの外） */}
      <Route
        path="/register"
        element={
         /* <PublicRoute> */
            <Register />
          /* </PublicRoute> */
        }
      />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      {/* 認証必須エリア */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Dashboard />} />
        <Route path="/devices" element={<DeviceList />} />
        <Route path="/devices/:thingName" element={<DeviceDetail />} />
        <Route path="/provision" element={<Provision />} />
        <Route path="/groups" element={<Groups />} />
        <Route path="/firmware" element={<FirmwareVersions />} />
        <Route path="/firmware/devices" element={<DeviceFirmware />} />
        <Route path="/ota" element={<OTAJobs />} />
        <Route path="/ota/:jobId" element={<OTAJobDetail />} />
        <Route path="/pipeline" element={<Pipeline />} />
        <Route path="/pipeline/:pipelineId" element={<Pipeline />} />
        <Route path="/sitl-tests" element={<SiTLTests />} />
        <Route path="/hils-tests" element={<HiLSTests />} />
        <Route path="/thing-versions" element={<ThingVersions />} />
        <Route path="/flight-logs" element={<FlightLogs />} />
        <Route path="/settings/organization" element={<OrganizationSettings />} />
        {/* superadmin */}
        <Route
          path="/admin"
          element={
            <SuperadminRoute>
              <Admin />
            </SuperadminRoute>
          }
        />
      </Route>

      {/* 404 → ダッシュボードへ */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
